import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const METRICS = [
  { key: "overall",   label: "Ovr" },
  { key: "finishing", label: "Fin" },
  { key: "passing",   label: "Pas" },
  { key: "defending", label: "Def" },
];

// Phase 12.1 — per-user persistence keys. The transfer-sim picks are sticky
// across player pages until the user changes them or resets.
const STORE_KEY = "fbm.transferSim";
function readStore(userId) {
  try {
    const raw = localStorage.getItem(`${STORE_KEY}:${userId || "anon"}`);
    if (!raw) return { leagueId: "", teamId: "", enabled: false };
    const parsed = JSON.parse(raw);
    return {
      leagueId: parsed.leagueId || "",
      teamId: parsed.teamId || "",
      enabled: !!parsed.enabled,
    };
  } catch {
    return { leagueId: "", teamId: "", enabled: false };
  }
}
function writeStore(userId, patch) {
  try {
    const k = `${STORE_KEY}:${userId || "anon"}`;
    const cur = readStore(userId);
    localStorage.setItem(k, JSON.stringify({ ...cur, ...patch }));
  } catch { /* localStorage disabled — fall back to ephemeral */ }
}

function tone(v) {
  if (v == null) return "text-muted-foreground";
  if (v >= 70) return "text-emerald-500";
  if (v >= 40) return "text-amber-500";
  return "text-rose-500";
}

/**
 * Phase 12 — Transfer simulation infobox.
 * Props:
 *  - playerId, playerName
 *  - active, onActiveChange:  Apply checkbox state (lifted to parent)
 *  - onSimulationChange:      Parent receives the latest /whatif payload (or null)
 *
 * Phase 12.1: the selected league + team + checkbox state persist per user
 * in localStorage so the simulation auto-applies when the user navigates
 * to a different player's detail page.
 */
export default function TransferInfoBox({
  playerId,
  playerName,
  active,
  onActiveChange,
  onSimulationChange,
}) {
  const { user } = useAuth();
  const userId = user?.id;

  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  // Hydrate from localStorage ONCE on first mount (lazy init).
  const initialStore = useMemo(() => readStore(userId), [userId]);
  const [leagueId, setLeagueId] = useState(initialStore.leagueId);
  const [teamId, setTeamId] = useState(initialStore.teamId);
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [storeHydrated, setStoreHydrated] = useState(false);

  // Hydrate the parent's `active` state from localStorage on first mount.
  // We only push UP — never down — and only once.
  useEffect(() => {
    if (initialStore.enabled && !active) {
      onActiveChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Promise.all([api.get("/leagues"), api.get("/teams")])
      .then(([l, t]) => {
        setLeagues(l.data); setTeams(t.data);
        // Validate the hydrated team/league still exist; clear gracefully if not.
        const stillValidLeague = !initialStore.leagueId
          || l.data.some((x) => x.id === initialStore.leagueId);
        const stillValidTeam = !initialStore.teamId
          || t.data.some((x) => x.id === initialStore.teamId);
        if (!stillValidLeague || !stillValidTeam) {
          setLeagueId(""); setTeamId("");
          writeStore(userId, { leagueId: "", teamId: "", enabled: false });
          onActiveChange?.(false);
        }
        setStoreHydrated(true);
      })
      .catch(() => { setStoreHydrated(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascading: when league changes, clear team
  const teamsInLeague = useMemo(() => {
    if (!leagueId) return [];
    return teams.filter((t) => (t.league_ids || []).includes(leagueId));
  }, [teams, leagueId]);

  // Fetch the /whatif preview whenever a team is picked OR the player changes.
  useEffect(() => {
    if (!teamId) {
      setSim(null);
      onSimulationChange?.(null);
      return;
    }
    setLoading(true);
    const params = { new_team_id: teamId };
    if (leagueId) params.new_league_id = leagueId;
    api.get(`/players/${playerId}/whatif`, { params })
      .then((r) => {
        setSim(r.data);
        onSimulationChange?.(r.data);
      })
      .catch(() => { setSim(null); onSimulationChange?.(null); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, leagueId, playerId]);

  const handleLeagueChange = (v) => {
    const next = v === "__" ? "" : v;
    setLeagueId(next); setTeamId("");
    writeStore(userId, { leagueId: next, teamId: "" });
  };
  const handleTeamChange = (v) => {
    const next = v === "__" ? "" : v;
    setTeamId(next);
    writeStore(userId, { teamId: next });
  };
  const handleActiveChange = (v) => {
    const next = !!v;
    onActiveChange?.(next);
    writeStore(userId, { enabled: next });
  };
  const reset = () => {
    setLeagueId(""); setTeamId(""); setSim(null);
    onActiveChange?.(false); onSimulationChange?.(null);
    writeStore(userId, { leagueId: "", teamId: "", enabled: false });
  };

  const canApply = !!sim && !sim.error && sim.posterior_final;

  // Edge case: if the saved checkbox was on but the new player can't be
  // simulated against this team (canApply=false), auto-disable the checkbox.
  useEffect(() => {
    if (active && !canApply && !loading && storeHydrated && teamId) {
      onActiveChange?.(false);
      writeStore(userId, { enabled: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApply, loading, storeHydrated, teamId]);

  return (
    <Card className="rounded-xl border-amber-500/30" data-testid="transfer-infobox">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-500/70 mb-1">Phase 12 · Bayesian</div>
            <h3 className="font-display uppercase tracking-tight text-lg" data-testid="transfer-title">Transfer simulation</h3>
            <p className="text-xs text-muted-foreground mt-0.5">What if {playerName || "this player"} joined another team?</p>
          </div>
          {(leagueId || teamId) && (
            <Button variant="ghost" size="sm" onClick={reset} className="h-8" data-testid="transfer-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">New league</Label>
            <Select value={leagueId || "__"} onValueChange={handleLeagueChange}>
              <SelectTrigger data-testid="transfer-league-select"><SelectValue placeholder="Pick a league…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__">— select league —</SelectItem>
                {leagues.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">New team</Label>
            <Select value={teamId || "__"} onValueChange={handleTeamChange} disabled={!leagueId}>
              <SelectTrigger data-testid="transfer-team-select"><SelectValue placeholder={leagueId ? "Pick a team…" : "Pick a league first"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__">— select team —</SelectItem>
                {teamsInLeague
                  .filter((t) => !sim?.current_team || t.id !== sim.current_team.id)
                  .map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Checkbox id="transfer-apply" checked={active && canApply} disabled={!canApply}
              onCheckedChange={handleActiveChange} data-testid="transfer-apply-checkbox" />
            <Label htmlFor="transfer-apply" className="text-xs cursor-pointer">
              Apply to FBM tiles, Distributions & Comparison
            </Label>
          </div>
        </div>

        {/* Status + live preview */}
        <div className="mt-4 min-h-[42px]">
          {loading && <div className="text-xs text-muted-foreground" data-testid="transfer-loading">Simulating…</div>}
          {!loading && sim?.error && (
            <div className="text-xs text-rose-500" data-testid="transfer-error">{sim.error}</div>
          )}
          {!loading && sim && !sim.error && sim.posterior_final && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="transfer-preview">
              <span className="text-muted-foreground">
                <span className="text-foreground/80">{sim.current_team?.name}</span> ({sim.current_team?.fbm_score}) <ArrowRight className="h-3 w-3 inline mx-1" />
                <span className="text-foreground/80">{sim.new_team?.name}</span> ({sim.new_team?.fbm_score})
                {sim.current_league?.id !== sim.new_league?.id && (
                  <> · <span className="text-foreground/80">{sim.current_league?.name}</span> ({sim.current_league?.fbm_league_score}) <ArrowRight className="h-3 w-3 inline mx-1" /> <span className="text-foreground/80">{sim.new_league?.name}</span> ({sim.new_league?.fbm_league_score})</>
                )}
              </span>
              <span className="inline-flex items-center gap-3 px-3 py-1 rounded border border-border bg-muted/30">
                {METRICS.map((m) => {
                  const before = sim.current?.[m.key];
                  const after = sim.posterior_final?.[m.key];
                  return (
                    <span key={m.key} className="font-mono" data-testid={`transfer-preview-${m.key}`}>
                      <span className="text-muted-foreground">{m.label}</span>{" "}
                      <span className="text-muted-foreground line-through">{before}</span>{" "}
                      <span className={tone(after)}>{after}</span>
                    </span>
                  );
                })}
              </span>
            </div>
          )}
        </div>

        {/* Phase 16 — Subjective 12-month prediction log. Append-only POST
            to /predictions. Disabled until a league AND team are selected. */}
        <PredictionLogger
          playerId={playerId}
          targetTeamId={teamId}
          targetLeagueId={leagueId}
        />
      </CardContent>
    </Card>
  );
}


// =====================================================================
// Phase 16 — Prediction logger
// =====================================================================

const PREDICTION_FIELDS = [
  { key: "predicted_overall",   label: "Overall",      simKey: "simulated_overall" },
  { key: "predicted_finishing", label: "Finishing",    simKey: "simulated_finishing" },
  { key: "predicted_passing",   label: "Passing Game", simKey: "simulated_passing" },
  { key: "predicted_defending", label: "Defending",    simKey: "simulated_defending" },
];

const EMPTY_PREDICTION = {
  predicted_overall: "",
  predicted_finishing: "",
  predicted_passing: "",
  predicted_defending: "",
};

// Phase 16.2 — render the saved timestamp in a human-friendly format.
function formatSavedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: "UTC", timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function formatModelStat(v) {
  if (v == null) return "—";
  return Number(v).toFixed(1);
}

function PredictionLogger({ playerId, targetTeamId, targetLeagueId }) {
  const [values, setValues] = useState(EMPTY_PREDICTION);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Phase 16.2 — current user's most recent prediction for THIS player.
  // null = "no prediction yet" → block stays hidden.
  const [latest, setLatest] = useState(null);

  const canSubmit =
    !!playerId && !!targetTeamId && !!targetLeagueId && !submitting;
  const targetMissing = !targetTeamId || !targetLeagueId;

  // Fetch latest on mount and whenever the player changes. 204 → null.
  useEffect(() => {
    if (!playerId) {
      setLatest(null);
      return;
    }
    let cancelled = false;
    api
      .get("/predictions/mine/latest", { params: { player_id: playerId } })
      .then((r) => {
        if (cancelled) return;
        // 204 has no body → axios delivers `data` as empty string / null.
        const data = r.data;
        setLatest(data && typeof data === "object" && data.id ? data : null);
      })
      .catch(() => {
        if (!cancelled) setLatest(null);
      });
    return () => { cancelled = true; };
  }, [playerId]);

  function handleChange(key, raw) {
    setError("");
    // Keep an empty string until the user types — let HTML5 number input
    // narrow further; we validate strictly on submit.
    setValues((v) => ({ ...v, [key]: raw }));
  }

  function validateLocally() {
    for (const f of PREDICTION_FIELDS) {
      const raw = values[f.key];
      if (raw === "" || raw === null || raw === undefined) {
        return "Fill all four fields.";
      }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 1 || n > 99) {
        return "Each prediction must be a whole number between 1 and 99.";
      }
    }
    return "";
  }

  async function handleSubmit() {
    const localErr = validateLocally();
    if (localErr) {
      setError(localErr);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        player_id: playerId,
        target_team_id: targetTeamId,
        target_league_id: targetLeagueId,
      };
      for (const f of PREDICTION_FIELDS) {
        payload[f.key] = Number(values[f.key]);
      }
      const res = await api.post("/predictions", payload);
      toast.success("Prediction saved");
      setValues(EMPTY_PREDICTION);
      // Phase 16.2 — optimistic refresh: POST already returns the full
      // record (snapshots + sim fields), so we avoid the extra GET.
      if (res?.data?.id) setLatest(res.data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="mt-6 pt-5 border-t border-border/60"
      data-testid="prediction-logger"
    >
      {latest && (
        <LatestPredictionBlock latest={latest} />
      )}

      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Your 12-month prediction
          </div>
          <div className="text-xs text-muted-foreground/80 mt-0.5">
            Append-only log. Each save creates a new record.
          </div>
        </div>
        {targetMissing && (
          <span
            className="text-[10px] uppercase tracking-wider text-amber-500"
            data-testid="prediction-disabled-hint"
          >
            Set a simulation target first to log a prediction
          </span>
        )}
      </div>

      <fieldset
        disabled={targetMissing}
        className={targetMissing ? "opacity-50 pointer-events-none" : ""}
        data-testid="prediction-fieldset"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PREDICTION_FIELDS.map((f) => (
            <div key={f.key}>
              <Label
                htmlFor={`pred-${f.key}`}
                className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
              >
                {f.label}
              </Label>
              <div className="relative mt-1">
                <Input
                  id={`pred-${f.key}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={99}
                  step={1}
                  value={values[f.key]}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  placeholder="1–99"
                  disabled={targetMissing}
                  className="pr-7 font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`prediction-input-${f.key}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  %
                </span>
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div
            className="mt-3 text-xs text-rose-500"
            data-testid="prediction-error"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="prediction-save-button"
          >
            {submitting ? "Saving…" : "Save Prediction"}
          </Button>
        </div>
      </fieldset>
    </div>
  );
}


// Phase 16.2 — confirmation block displaying the user's most recent saved
// prediction for the current player. Subordinate to the input form: small
// muted background, smaller text, light border.
function LatestPredictionBlock({ latest }) {
  return (
    <div
      className="mb-5 rounded border border-border/60 bg-muted/20 px-4 py-3"
      data-testid="latest-prediction-block"
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Your most recent prediction
        </div>
        <div
          className="text-[10px] uppercase tracking-wider text-muted-foreground/80"
          data-testid="latest-prediction-saved-at"
        >
          saved {formatSavedAt(latest.created_at)}
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        For{" "}
        <span
          className="text-foreground/85"
          data-testid="latest-prediction-team"
        >
          {latest.target_team_name_snapshot}
        </span>{" "}
        in{" "}
        <span
          className="text-foreground/85"
          data-testid="latest-prediction-league"
        >
          {latest.target_league_name_snapshot}
        </span>
      </div>
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground/80">
            <th className="text-left font-normal pb-1">Stat</th>
            <th className="text-right font-normal pb-1">Your %</th>
            <th className="text-right font-normal pb-1">Model</th>
          </tr>
        </thead>
        <tbody>
          {PREDICTION_FIELDS.map((f) => (
            <tr key={f.key} className="border-t border-border/30">
              <td className="py-1 text-foreground/80">{f.label}</td>
              <td
                className="py-1 text-right"
                data-testid={`latest-prediction-scout-${f.key}`}
              >
                {latest[f.key]}%
              </td>
              <td
                className="py-1 text-right text-muted-foreground"
                data-testid={`latest-prediction-model-${f.simKey}`}
              >
                {formatModelStat(latest[f.simKey])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
